import type {
  GTMEventGlycerinCampaignClick,
  GTMEventGlycerinDisplayCampaign,
} from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { useRouter } from 'next/router';

type BehaviorProps = {
  behavior_paragraph_campaign_type?: string | null;
  behavior_paragraph_campaignid?: string | null;
  // for flyout-menu and usp-bar the campaign-id is underscored
  behavior_paragraph_campaign_id?: string | null;
  behavior_paragraph_category?: string | null;
  behavior_paragraph_detail?: string | null;
  behavior_paragraph_name?: string | null;
};

type PromotionProps = {
  glycerinCampaignType?: string;
  glycerinCampaignId?: string;
  glycerinCategory?: string;
  glycerinDetail?: string;
  glycerinName?: string;
  linkReady?: string;
};

type GlycerinCampaignTracking = BehaviorProps | PromotionProps;

/**
 * execute glycerin tracking for campaign display and click
 *
 * @param object glycerin tracking information
 * @returns function to execute the dispatcher on click
 */
const useTrackCampaign = (data: GlycerinCampaignTracking, target?: string) => {
  let finalTrackingData: {
    campaignType?: string;
    category?: string;
    campaignId?: string;
    name?: string;
    trackingTarget?: string;
    detail?: string;
  } = {};
  if ('behavior_paragraph_campaign_type' in data) {
    finalTrackingData = {
      campaignType: data.behavior_paragraph_campaign_type || undefined,
      category: data.behavior_paragraph_category || undefined,
      campaignId:
        data.behavior_paragraph_campaignid || data.behavior_paragraph_campaign_id || undefined,
      name: data.behavior_paragraph_name || undefined,
      trackingTarget: target,
      detail: data.behavior_paragraph_detail || undefined,
    };
  } else if ('glycerinCampaignType' in data) {
    finalTrackingData = {
      campaignType: data.glycerinCampaignType,
      category: data.glycerinCategory,
      campaignId: data.glycerinCampaignId,
      name: data.glycerinName,
      trackingTarget: data.linkReady,
      detail: data.glycerinDetail,
    };
  }
  const { campaignType, category, campaignId, name, trackingTarget, detail } = finalTrackingData;
  const dispatchGtmEvent = useTracking();
  const { locale, defaultLocale, basePath } = useRouter();
  if (campaignType && category) {
    dispatchGtmEvent<GTMEventGlycerinDisplayCampaign>({
      event: 'DisplayCampaign',
      DisplayCampaignData: {
        campaignType,
        ...(campaignId && { campaignId }),
        ...(name && { name }),
        category,
      },
    });
  }
  if (campaignType && category) {
    let targetUrl: string | undefined;
    try {
      targetUrl = trackingTarget && new URL(trackingTarget, basePath).toString();
    } catch {
      // do nothing
    }
    return () =>
      dispatchGtmEvent<GTMEventGlycerinCampaignClick>({
        event: 'CampaignClick',
        CampaignClickData: {
          campaignType,
          category,
          ...(campaignId && { campaignId }),
          ...(detail && { detail }),
          ...(name && { name }),
          label: locale || defaultLocale || 'de',
          ...(targetUrl && { target: targetUrl }),
        },
      });
  }
  return () => {};
};

export { useTrackCampaign };
